// import { checkStatus, returnJson } from '@/helpers/api'
import { ATTACHMENT_ID, validAttachmentIDs } from '@/../shared/valueholders/attachments'
import { Bugfender } from '@bugfender/sdk'
import { mapGetters } from 'vuex'
import { isNumeric } from '@/helpers/string'
import { updateImagesByView } from '@/../shared/API/images'

export default {
  data() {
    return {
      loading: {
        down: false,
        up: false,
        remove: false,
      },
      attachments: {
        [ATTACHMENT_ID.TOP]: {
          id: ATTACHMENT_ID.TOP,
          file: null,
          filename: null,
          type: null,
          url: null,
          removed: false,
          changed: false,
        },
        [ATTACHMENT_ID.SIDE]: {
          id: ATTACHMENT_ID.SIDE,
          file: null,
          filename: null,
          type: null,
          url: null,
          removed: false,
          changed: false,
        },
      },
      bulkUploadItems: [],
      bulkUploadFails: [],
    }
  },
  computed: {
    ...mapGetters('planmode', [
      'getChargingPoints',
    ]),
    isLoadingAws() {
      return this.loading.down || this.loading.up || this.loading.remove
    },
  },
  methods: {
    /**
     * PUBLIC METHODS
     */
    $_chargingpointAttachmentsMixin_handleDrop(event, id) {
      this.isDragging = false

      if (!event.dataTransfer.files.length > 0) {
        return
      }

      if (id === ATTACHMENT_ID.BULK) {
        this.processMultipleFiles(event.dataTransfer.files)
        return
      }

      this.processFiles(event.dataTransfer.files, id)
    },

    async $_chargingpointAttachmentsMixin_handleFileChange(event, id) {
      if (!event.target.files.length > 0) {
        return
      }

      if (id === ATTACHMENT_ID.BULK) {
        this.processMultipleFiles(event.target.files)
        return
      }

      await this.processFiles(event.target.files, id)
    },

    async $_chargingpointAttachmentsMixin_bulkUploadAttachments() {
      this.loading.up = true

      // reset upload status
      this.bulkUploadItems.forEach(item => item.uploaded = false)

      try {
        const token = await this.$auth.getTokenSilently()

        for (const item of this.bulkUploadItems) {
          const result = await this.uploadAttachment({
            file: item.file,
            data: {
              code: item.code,
              id: item.id,
              uuid: item.uuid,
              view: item.view,
              type: item.file.type,
              size: item.file.size,
            },
          })

          const images = result?.images?.[item.view]
          const uploadedImage = images?.[images.length - 1]
          if (uploadedImage) {
            await updateImagesByView({
              uuid: item.uuid,
              imageId: uploadedImage.index,
              view: item.view,
              token,
            })
          }
        }

        this.$notify({
          type: 'success',
          title: 'Upload is klaar!',
        })
      } catch (e) {
        console.error('Error during bulk upload', e)
        this.$notify({
          type: 'error',
          title: 'Bulk upload mislukt',
          text: e.message || e,
        })
      } finally {
        this.loading.up = false
      }
    },

    async $_chargingpointAttachmentsMixin_uploadAttachment({ attachment }) {
      this.loading.up = true

      const id = attachment.id

      if (!validAttachmentIDs.includes(id)) {
        return
      }

      if (!this.attachments[id].file) {
        return
      }

      await this.uploadAttachment({
        file: attachment.file,
        data: {
          code: this.getActiveMunicipality,
          id: this.chargingpoint.properties.id,
          uuid: this.chargingpoint.uuid,
          view: id,
          type: attachment.file.type,
          size: attachment.file.size,
        },
      })

      this.attachments[id].type = attachment.file?.type?.split('/')?.[1]
      this.attachments[id].removed = false

      this.loading.up = false
    },

    $_chargingpointAttachmentsMixin_handleRemoveItemFromBulk(item) {
      const index = this.bulkUploadItems.find(x => x.uuid === item.uuid && x.view === item.view)
      this.bulkUploadItems.splice(index, 1)
    },

    /**
     * HELPER
     */
    resetAttachment({ id }) {
      if (!this.attachments || !this.attachments[id]) {
        return
      }
      // RESET THUMBNAIL
      this.attachments[id].file = null
      this.attachments[id].filename = null
      this.attachments[id].url = null
      this.attachments[id].changed = false
      this.attachments[id].removed = false
    },

    processMultipleFiles(files) {
      files.forEach(file => {
        const topViewTerms = [
          'bovenaanzicht',
          'top',
        ]
        const sideViewTerms = [
          'zijaanzicht',
          'side',
        ]

        const isSideView = sideViewTerms.some(term => file.name.search(term) !== -1)
        const isTopView = topViewTerms.some(term => file.name.search(term) !== -1)

        let view = false
        if (isSideView) {
          view = ATTACHMENT_ID.SIDE
        } else if (isTopView) {
          view = ATTACHMENT_ID.TOP
        }

        const regexSplitName = /(\d{4})(-|_)(\d*)/
        const matches = file.name.match(regexSplitName)

        if (matches === null) {
          return this.$store.dispatch('config/setToast', {
            vm: this,
            message: this.$t('components.upload.SuperUserUploadPanel.wrongFile', { name: file.name }),
            error: false,
            variant: 'warning',
            delay: 100,
          })
        }

        const code = matches[1]
        const id = parseInt(matches[3])

        // validate
        if (
          !isNumeric(code) ||
          !isNumeric(id) || typeof id !== 'number'
        ) {
          Bugfender.warn(`process file attachment: invalid code or id for file: ${file.name}`)
          return
        }

        if (id === false) {
          Bugfender.warn(`process file attachment: invalid attachment view for file: ${file.name}`)
          return
        }

        const point = this.getChargingPoints.find(chargingpoint => {
          return chargingpoint.code === code && chargingpoint.properties.id === id
        })

        if (!point) {
          Bugfender.warn(`process file attachment: can't find chargingpoint for file: ${file.name}`)
          return
        }

        const uuid = point.uuid

        this.bulkUploadItems.push({
          code,
          id,
          uuid,
          view,
          file,
          url: URL.createObjectURL(file),
          uploaded: false,
        })
      })
    },

    async processFiles(files, id) {
      if (!validAttachmentIDs.includes(id)) {
        return
      }

      for (const file of files) {
        // update attachments state per file
        this.attachments[id].file = file
        this.attachments[id].url = URL.createObjectURL(file)

        // upload het bestand
        await this.$_chargingpointAttachmentsMixin_uploadAttachment({ attachment: this.attachments[id] })
      }
    },

    /*
    * S3 API
    */

    async uploadAttachment({ file, data }) {
      const formData = new FormData()

      formData.append('type', data.view)
      formData.append('source', 'manual')
      formData.append('file', file)
      try {
        const response = await fetch(`${process.env.VUE_APP_AWS_APIV3_BASE_URL}/chargingpoint/${data.uuid}/files`, {
          method: 'PUT',
          headers: {
            'x-api-key': process.env.VUE_APP_AWS_API_KEY,
          },
          body: formData,
        })

        const responseData = await response.json()
        const images = responseData?.data?.images

        const index = this.bulkUploadItems.findIndex(item => item.file.name === file.name)
        if (index !== -1) {
          this.bulkUploadItems[index].uploaded = true
        }

        this.attachments[data.view].type = file.type

        return { images }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('error in file upload', e)
        this.bulkUploadFails.push({ message: 'cant upload', error: e, file, data, uid: crypto.randomUUID(), })

        const index = this.bulkUploadItems.findIndex(item => item.uuid === data.uuid && item.view === data.view)
        if (index !== -1) {
          this.bulkUploadItems[index].error = true
        }
      }
    },
  },
}
